// bluaIQ — Diagnostic Takeoff v3 — DiagnosticSafetyBadges
// Renders the safety flag map returned by the API as red/green pill
// badges. These badges are the human-visible proof that the gate
// posture is intact in the running session.

import { ShieldAlert, ShieldCheck } from 'lucide-react'

export interface DiagnosticSafetyBadgesProps {
  flags: Record<string, unknown>
}

const ALLOWED_TO_BE_TRUE = new Set(['diagnostic_only', 'diagnostic_v3_enabled', 'legacy_enabled'])

const FLAG_LABELS: Record<string, string> = {
  diagnostic_only: 'Diagnostic only',
  diagnostic_v3_enabled: 'V3 surface enabled',
  legacy_enabled: 'Legacy takeoff enabled',
  takeoff_engine: 'Active engine',
  final_quantities_allowed: 'Final quantities',
  gate_3_allowed: 'Gate 3',
  step_b_allowed: 'Step B',
  real_step_b_allowed: 'Real Step B',
  boq_allowed: 'BOQ',
  purchase_quantity_allowed: 'Purchase quantity',
  quantity_allowed: 'Quantity output',
  accepted_measurement_evidence_allowed: 'Accepted MeasurementEvidence',
  accepted_final_quantity_allowed: 'Accepted final quantity',
  accepted_parent_boundary_allowed: 'Accepted parent boundary',
  accepted_local_segment_width_allowed: 'Accepted local segment width',
  accepted_opening_quantities_allowed: 'Accepted opening quantities',
  final_scale_lock_record_allowed: 'Final ScaleLockRecord',
  takeoff_preview_allowed: 'Takeoff preview',
}

function isExpectedSafe(key: string, value: unknown): boolean {
  if (key === 'takeoff_engine') return true // string label, never a gate
  if (typeof value !== 'boolean') return true
  if (ALLOWED_TO_BE_TRUE.has(key)) return value === true
  return value === false
}

function formatValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (value == null) return '—'
  return String(value)
}

export default function DiagnosticSafetyBadges({ flags }: DiagnosticSafetyBadgesProps) {
  const entries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b))
  return (
    <div className="space-y-2">
      <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
        Safety posture
      </div>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([key, value]) => {
          const safe = isExpectedSafe(key, value)
          const label = FLAG_LABELS[key] ?? key
          const pillClass = safe
            ? 'bg-green-50 text-green-700 border border-green-100'
            : 'bg-red-50 text-red-700 border border-red-100'
          const Icon = safe ? ShieldCheck : ShieldAlert
          return (
            <span
              key={key}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-[800] ${pillClass}`}
              title={`${label} = ${formatValue(value)}`}
            >
              <Icon size={10} />
              {label}: {formatValue(value)}
            </span>
          )
        })}
      </div>
    </div>
  )
}
